import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { logActivity } from "@/lib/logActivity";
import ContractModal, { type Contract, type ContractSavePayload } from "@/pages/admin/contracts/components/ContractModal";
import PaymentStatusBadge from "@/components/base/PaymentStatusBadge";
import StatusBadge from "@/components/base/StatusBadge";
import Swal from "sweetalert2";
import type { Client, ClientContract } from "../types";

interface Props {
  client: Client;
  tenantId: string;
}

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function ContractsTab({ client, tenantId }: Props) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<ClientContract[]>([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [clients, setClients]     = useState<{ id: string; full_name: string }[]>([]);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("contracts")
      .select("id, contract_number, title, event_date, total_amount, deposit_amount, balance_amount, status, payment_status, created_at")
      .eq("tenant_id", tenantId)
      .eq("client_id", client.id)
      .order("created_at", { ascending: false });
    setContracts((data as ClientContract[]) ?? []);
    setLoading(false);
  }, [client.id, tenantId]);

  useEffect(() => {
    fetchContracts();
    supabase.from("clients").select("id, full_name").eq("tenant_id", tenantId).eq("status", "active")
      .then(({ data }) => setClients((data as { id: string; full_name: string }[]) ?? []));
  }, [fetchContracts, tenantId]);

  const handleSave = async (payload: ContractSavePayload) => {
    const { contract } = payload;
    const contractNum = `CTR-${Date.now().toString().slice(-6)}`;
    const balance = contract.total_amount - contract.deposit_amount;
    const { data: created, error } = await supabase
      .from("contracts")
      .insert({
        tenant_id: tenantId,
        client_id: client.id,
        client_name: client.full_name,
        contract_number: contractNum,
        title: contract.title,
        event_date: contract.event_date || "",
        event_location: contract.event_location || "",
        package_name: contract.package_name || "",
        total_amount: contract.total_amount,
        deposit_amount: contract.deposit_amount,
        balance_amount: balance > 0 ? balance : 0,
        status: "draft",
        payment_status: "unpaid",
      })
      .select("id, contract_number, title, event_date, total_amount, deposit_amount, balance_amount, status, payment_status, created_at")
      .maybeSingle();
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#fff", color: "#0f172a" });
      return;
    }
    if (created) {
      setContracts((prev) => [created as ClientContract, ...prev]);
      logActivity({ tenantId, action: "created", entity: "contract", entityId: (created as ClientContract).id, entityName: contract.title });
    }
    setModalOpen(false);
    Swal.fire({ title: "Contract created!", icon: "success", timer: 1500, showConfirmButton: false, background: "#fff", color: "#0f172a" });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-slate-400 text-sm">{contracts.length} contract{contracts.length !== 1 ? "s" : ""}</p>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          New Contract
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center">
          <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-400 rounded-full animate-spin"></div>
          Loading contracts…
        </div>
      ) : contracts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
            <i className="ri-file-text-line text-[#9a7840] text-xl"></i>
          </div>
          <p className="text-slate-700 font-medium text-sm">No contracts yet</p>
          <p className="text-slate-400 text-xs">Create the first contract for {client.full_name}</p>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Create Contract
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {contracts.map((c) => (
            <div key={c.id} className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all p-4 flex items-center gap-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 shrink-0">
                <i className="ri-file-text-line text-[#9a7840] text-base"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <p className="text-slate-800 font-medium text-sm truncate">{c.title}</p>
                  <span className="text-slate-300 text-xs font-mono">{c.contract_number}</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap text-xs text-slate-400">
                  {c.event_date && <span><i className="ri-calendar-line mr-1"></i>{fmtDate(c.event_date)}</span>}
                  <span><i className="ri-money-dollar-circle-line mr-1"></i>${c.total_amount?.toLocaleString()}</span>
                  {c.balance_amount > 0 && <span className="text-amber-500">Balance: ${c.balance_amount?.toLocaleString()}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={c.status} size="sm" />
                <PaymentStatusBadge status={c.payment_status} />
                <button
                  onClick={() => navigate(`/admin/contracts/${c.id}`)}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#9a7840] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                  title="Open contract"
                >
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <ContractModal
          contract={null}
          isNew
          tenantId={tenantId}
          clients={[{ id: client.id, full_name: client.full_name }, ...clients.filter((c) => c.id !== client.id)]}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}